/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT Licence.
 * See LICENSE in the project root for licence information.
 */

/* global Office */

import { aiDraft, AiDraftRequest, AiDraftOption } from "../services/ai.service";
import { getDocumentInfo } from "../utils/document.utils";
import { convertMarkdownToHtml } from "../utils/markdown.utils";
import { getAccessToken } from "../utils/storage.utils";
import { toast } from "sonner";

Office.onReady(() => {
  // If needed, Office.js is ready to be called.
});

let dialog: Office.Dialog;

function showLoadingDialog(): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      "https://localhost:3000/loading.html",
      { height: 25, width: 25, displayInIframe: true },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Failed to show dialog:", asyncResult.error.message);
          reject(asyncResult.error.message);
        } else {
          dialog = asyncResult.value;
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => { });
          resolve();
        }
      }
    );
  });
}

function closeLoadingDialog() {
  if (dialog) {
    dialog.close();
  }
}



// Helper to check if error is 401
function isUnauthorizedError(error: any): boolean {
  return error.response && error.response.status === 401;
}

// Helper to open the taskpane
async function openTaskpane() {
  if (Office.addin && Office.addin.showAsTaskpane) {
    return Office.addin.showAsTaskpane().catch((error) => {
      console.error("Failed to show taskpane:", error);
    });
  } else {
    console.warn("Office.addin.showAsTaskpane is not available.");
    return Promise.resolve();
  }
}

/**
 * Common handler for AI actions
 */
async function handleAiAction(option: AiDraftOption, event: Office.AddinCommands.Event) {
  console.log(`${option} action triggered`);
  try {
    // Check for access token before proceeding
    const token = await getAccessToken();
    if (!token) {
      console.log("No access token found. Opening taskpane.");
      await openTaskpane();
      toast.error("Please log in to use this feature.");
      event.completed(); // Important: signal completion to Office
      return;
    }

    // Show loading dialog
    await showLoadingDialog();

    const documentInfo = await getDocumentInfo();
    const documentId = documentInfo.id;
    console.log(`Document ID: ${documentId}`);

    Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, async (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const selectedText = result.value;
        console.log(`Selected Text: ${selectedText}`);

        if (selectedText) {
          try {
            const request: AiDraftRequest = {
              document_id: documentId || "",
              text_selected: selectedText as string,
            };
            const response = await aiDraft(option, request);
            const htmlResponse = convertMarkdownToHtml(response.response);
            Office.context.document.setSelectedDataAsync(htmlResponse, { coercionType: Office.CoercionType.Html }, (asyncResult) => {
              if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                console.error("Failed to insert text:", asyncResult.error.message);
              }
              closeLoadingDialog(); // Close dialog on success
            });
          } catch (apiError) {
            console.error("API Error:", apiError);
            closeLoadingDialog(); // Always close loading dialog first

            if (isUnauthorizedError(apiError)) {
              console.log("Unauthorized (401). Attempting to open taskpane.");
              await openTaskpane();
            }
          }
        } else {
          console.log("No text selected.");
          closeLoadingDialog(); // Close dialog if no text
        }
      } else {
        console.error("Failed to get selected text:", result.error);
        closeLoadingDialog(); // Close dialog on selection error
      }
      event.completed();
    });
  } catch (error) {
    console.error("Error in handler:", error);
    closeLoadingDialog(); // Close dialog on general error
    event.completed();
  }
}

async function writeClause(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.WriteClause, event);
}

async function writeDefinition(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.WriteDefinition, event);
}

async function makeShorter(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.MakeShorter, event);
}

async function makeLonger(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.MakeLonger, event);
}

async function simplifyLanguage(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.SimplifyLanguage, event);
}

async function complexifyLanguage(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.ComplexifyLanguage, event);
}

async function summarise(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.Summarise, event); // Note: API endpoint uses 'summarizer' based on Vue component
}

// Register the functions with Office.


Office.actions.associate("writeClause", writeClause);
Office.actions.associate("writeDefinition", writeDefinition);
Office.actions.associate("makeShorter", makeShorter);
Office.actions.associate("makeLonger", makeLonger);
Office.actions.associate("simplifyLanguage", simplifyLanguage);
Office.actions.associate("complexifyLanguage", complexifyLanguage);
Office.actions.associate("summarise", summarise);
